export interface Device {
  id: string;
  name: string;
  model: string;
  role: "server" | "player";
  status: "online" | "offline";
  currentVersion: string;
  lastSeen: string;
  channel: "stable" | "beta";
  updateAvailable: string | null;
  licenseKey: string;
}

export interface DevicesResponse {
  devices: Device[];
  licenseKey: string;
  email: string;
}

export type ChannelName = "stable" | "beta";
export type ComponentName = "orchestrator" | "web" | "player";

/**
 * Single signed download. Per-component rows carry exactly one of these;
 * bundle rows carry one as well (the `bundle.tar.zst` archive).
 */
export interface ReleaseArtifact {
  name: string;
  filename?: string;
  url: string;
  size: number;
  sha256: string;
}

/**
 * V2 release-list shape from the worker (`/api/releases/public` and
 * `/api/user/releases`). One entry per (channel, version) — bundle rows
 * stand alone, per-component rows are already collapsed into a single
 * card by the worker when multiple components share a version.
 */
export interface Release {
  version: string;
  channel: ChannelName;
  component: "bundle" | ComponentName | null;
  date: string;
  notes: string;
  customerNotes: string | null;
  /** Bundle rollout percent (0..100). `null` for per-component rows. */
  rolloutPercent: number | null;
  rolloutStartedAt: string | null;
  /** Pinned component versions for a bundle row. `null` for component rows. */
  componentVersions: Partial<Record<ComponentName, string>> | null;
  artifacts: ReleaseArtifact[];
  isLatest: boolean;
}

export interface ReleasesResponse {
  releases: Release[];
}

/**
 * The `/api/releases/bundles/current/:channel` payload. This is the one
 * endpoint that drives the bundle hero card on Home and the bundle detail
 * page. Customer-facing, so `customerReleaseNotes` is the primary body.
 */
export interface BundlePointer {
  version: string;
  channel: ChannelName;
  releasedAt: string;
  rolloutPercent: number;
  rolloutStartedAt: string | null;
  bundle: {
    url: string;
    sha256: string;
    signature: string;
    sizeBytes: number;
  };
  manifest: {
    url: string;
    sha256: string;
  };
  componentVersions: Record<ComponentName, string>;
  releaseNotes: string | null;
  customerReleaseNotes: string | null;
  commitSha: string | null;
  ciPipelineId: string | null;
}

/**
 * `/api/releases/pointers/:channel` — bundle pointer plus per-component
 * channel tips. Used on the bundle detail page to deep-link into each
 * component manifest.
 */
export interface ComponentPointer {
  version: string;
  channel: string;
  releasedAt: string;
  releaseNotes?: string | null;
  customerReleaseNotes?: string | null;
  commitSha?: string | null;
  ciPipelineId?: string | null;
  artifact?: {
    filename: string;
    sha256: string;
    signature: string;
    sizeBytes: number;
    imageRef?: string;
  };
}

export interface ChannelPointers {
  channel: string;
  bundle: BundlePointer | null;
  components: Record<ComponentName, ComponentPointer | null>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
  });

  if (!res.ok) {
    throw new ApiError(res.status, await res.text());
  }

  return res.json() as Promise<T>;
}

export async function fetchDevices(): Promise<DevicesResponse> {
  const data = await request<DevicesResponse>("/api/user/devices");

  // Also fetch license key from the platform API (cross-domain with cookie)
  try {
    const licenseRes = await fetch("https://blazeboard.co/api/user/license", {
      credentials: "include",
    });
    if (licenseRes.ok) {
      const licenseData = await licenseRes.json();
      if (licenseData.licenseKey) {
        data.licenseKey = licenseData.licenseKey;
      }
    }
  } catch {
    // Platform unreachable — use whatever the Worker returned
  }

  return data;
}

/**
 * V2 wire shape from the worker. One row per (channel, version) — bundle
 * rows stand alone, per-component rows have already been aggregated by
 * the worker so their `artifacts[]` holds every component binary under
 * a single version umbrella.
 */
type WorkerRelease = {
  channel: string;
  version: string;
  released_at: string | null;
  release_notes: string | null;
  customerReleaseNotes?: string | null;
  rolloutPercent: number | null;
  rolloutStartedAt: string | null;
  component?: string | null;
  componentVersions?: Record<string, string> | null;
  artifacts: Array<{
    name: string;
    filename?: string;
    download_url: string;
    size_bytes?: number;
    sha256?: string;
  }>;
};

type WorkerReleaseList = {
  ok: boolean;
  releases: WorkerRelease[];
};

function mapWorkerReleases(raw: WorkerReleaseList): ReleasesResponse {
  const rows = raw.releases || [];
  // "Latest" per channel is the newest bundle in that channel — if a
  // channel has no bundle yet we fall back to the newest per-component
  // row so the UI still flags something as current.
  const latestByChannel = new Map<string, string>();
  for (const r of rows) {
    if (r.component === "bundle" && !latestByChannel.has(r.channel)) {
      latestByChannel.set(r.channel, r.version);
    }
  }
  for (const r of rows) {
    if (!latestByChannel.has(r.channel)) {
      latestByChannel.set(r.channel, r.version);
    }
  }

  const releases: Release[] = rows.map((r) => {
    const channel = (r.channel as ChannelName) || "stable";
    const rawComponent = r.component ?? null;
    const component =
      rawComponent === "bundle" ||
      rawComponent === "orchestrator" ||
      rawComponent === "web" ||
      rawComponent === "player"
        ? rawComponent
        : null;
    return {
      version: r.version,
      channel,
      component,
      date: r.released_at || new Date().toISOString(),
      notes: r.release_notes || "",
      customerNotes: r.customerReleaseNotes ?? null,
      rolloutPercent: r.rolloutPercent,
      rolloutStartedAt: r.rolloutStartedAt,
      componentVersions:
        r.componentVersions && component === "bundle"
          ? (r.componentVersions as Partial<Record<ComponentName, string>>)
          : null,
      artifacts: (r.artifacts || []).map((a) => ({
        name: a.name,
        filename: a.filename,
        url: a.download_url,
        size: a.size_bytes ?? 0,
        sha256: a.sha256 ?? "",
      })),
      isLatest: latestByChannel.get(channel) === r.version,
    };
  });
  return { releases };
}

/**
 * Changelog fetch — tries the user-scoped endpoint first (rich data when
 * signed in), falls back to the public endpoint on 401/403 so anonymous
 * visitors still see the changelog.
 */
export async function fetchReleases(): Promise<ReleasesResponse> {
  try {
    const raw = await request<WorkerReleaseList>("/api/user/releases");
    return mapWorkerReleases(raw);
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      return fetchPublicReleases();
    }
    throw err;
  }
}

export async function fetchPublicReleases(): Promise<ReleasesResponse> {
  const raw = await request<WorkerReleaseList>("/api/releases/public");
  return mapWorkerReleases(raw);
}

/**
 * Current bundle pointer for a channel — the headline the homepage
 * leads with. Returns `null` on 404 (e.g. a channel that hasn't cut
 * its first bundle yet).
 */
export async function fetchBundlePointer(
  channel: ChannelName,
): Promise<BundlePointer | null> {
  try {
    const raw = await request<{ ok: boolean; bundle: BundlePointer }>(
      `/api/releases/bundles/current/${channel}`,
    );
    return raw.bundle;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** Bundle pointer + per-component channel tips for a channel. */
export async function fetchChannelPointers(
  channel: ChannelName,
): Promise<ChannelPointers> {
  const raw = await request<{
    ok: boolean;
    channel: string;
    bundle: BundlePointer | null;
    components: Record<ComponentName, ComponentPointer | null>;
  }>(`/api/releases/pointers/${channel}`);
  return {
    channel: raw.channel,
    bundle: raw.bundle,
    components: raw.components,
  };
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
